import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { employeeProfileKeys } from "@/lib/query";
import type {
  EmployeeProfileCreateInput,
  EmployeeProfileUpdateInput,
} from "@/schemas/employee-profiles";

export interface EmployeeProfile {
  id: string;
  name: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
  /**
   * The NOS `EmployeeProfileID`, or null for a profile added in Enable that NOS
   * has no counterpart for. A person holding an unlinked profile can't be
   * written back to NOS, so the admin page says so rather than leaving it to be
   * discovered in the NOS sync log.
   */
  nosEmployeeProfileId: string | null;
  /** How many people hold this profile — a profile in use can't be deleted. */
  personCount: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Every employee profile, active and inactive, in admin order.
 *
 * This backs the admin page only. The person form's picker goes through the
 * shared `EntityCombobox` → `/api/options/employee-profiles`, which filters to
 * active rows server-side — so there is no second copy of the list here.
 */
export function useEmployeeProfiles(enabled = true) {
  return useQuery({
    queryKey: employeeProfileKeys.all,
    queryFn: async (): Promise<EmployeeProfile[]> => {
      const res = await fetch("/api/employee-profiles");
      if (!res.ok) throw new Error("Failed to fetch employee profiles");
      const json = await res.json();
      return json.data;
    },
    enabled,
  });
}

async function mutate(url: string, method: string, body?: unknown) {
  const res = await fetch(url, {
    method,
    ...(body !== undefined && {
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  });
  if (!res.ok) {
    // The delete and create refusals carry a specific message (a profile in
    // use, one linked to NOS, a duplicate name); surfacing it is the point.
    const json = await res.json().catch(() => null);
    throw new Error(json?.error ?? "Request failed");
  }
  return res.status === 204 ? null : res.json().catch(() => null);
}

export function useCreateEmployeeProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: EmployeeProfileCreateInput) =>
      mutate("/api/employee-profiles", "POST", input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeProfileKeys.all });
      toast.success("Employee profile created");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useUpdateEmployeeProfile(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: EmployeeProfileUpdateInput) =>
      mutate(`/api/employee-profiles/${id}`, "PUT", input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeProfileKeys.all });
      toast.success("Employee profile updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useReorderEmployeeProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, direction }: { id: string; direction: "up" | "down" }) =>
      mutate(`/api/employee-profiles/${id}/reorder`, "PATCH", { direction }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeProfileKeys.all });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useDeleteEmployeeProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => mutate(`/api/employee-profiles/${id}`, "DELETE"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeProfileKeys.all });
      toast.success("Employee profile deleted");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
